import React, { useEffect, useMemo, useState, useContext } from 'react';
import Skeleton from 'react-loading-skeleton';

import { organizationTypeCodes } from 'common/utils/organizationTypeCodes';
import { AccountSettingsContext } from '../screens/context';

import { useContentfulOrganizationDefaultContent } from 'common/api/contentful/organization';
import {
    CoordinatorData,
    getCoordinatorInfoByOrganizationCode,
} from 'common/api/crm/organization/coordinator';
import { createStyles } from 'utils/createStyle';
import { waitPromise } from 'utils/promiseHelpers';
import { useRootSelector } from 'rootStore';
import { selectHomeDetailsAsyncState } from 'common/features/store/duck/home/duck';

const styles = createStyles({
    headerLogoContainer: [
        'flex-shrink-0',
        {
            minHeight: '200px',
        },
    ],
    headerLogoImg: {
        borderRadius: '0 0 10px 10px',
        height: '100%',
        maxHeight: '200px',
    },
    text2: [
        'flex w-full',
        {
            maxWidth: '20rem',
        },
    ],
});

export const AccountSettingsHeader = () => {
    const { content: orgDefaultContent, isLoading: isLoadingOrgDefaultContent } =
        useContentfulOrganizationDefaultContent();
    const { setPageIsLoading } = useContext(AccountSettingsContext);
    const defaultOrg = useRootSelector((s) => s.store.home.homeDetails?.defaultOrganization);
    const homeDetailsAsyncState = useRootSelector((s) => selectHomeDetailsAsyncState(s));

    const [coordinator, setCoordinator] = useState<CoordinatorData>();

    const [isLoadingCoordinator, setIsLoadingCoordinator] = useState(false);

    const organizationInfo = useMemo(() => {
        if (!orgDefaultContent || !defaultOrg) {
            return;
        }
        const code = organizationTypeCodes[defaultOrg.id];
        return orgDefaultContent.items.find((it) => it.fields.id === code);
    }, [orgDefaultContent, defaultOrg]);

    useEffect(() => {
        (async () => {
            if (defaultOrg) {
                const organizationId = defaultOrg.id;
                const coordinatorInfo = await waitPromise(
                    getCoordinatorInfoByOrganizationCode(organizationId),
                    setIsLoadingCoordinator
                );
                setCoordinator(coordinatorInfo.data || undefined);
            } else {
                setCoordinator(undefined);
            }
        })();
    }, [defaultOrg, setCoordinator]);

    const isLoading = isLoadingOrgDefaultContent || !defaultOrg || homeDetailsAsyncState.loading;

    useEffect(() => {
        /* Side effect that sets the `pageIsLoading` value to false once
         * org content & contentful resources are loaded.
         * `pageIsLoading` is used to control the rendering of `AccountSettingsContent`
         */
        if (defaultOrg && !homeDetailsAsyncState.loading && !isLoadingOrgDefaultContent) {
            // NOTE: this will be called twice -- once to update the value from true -> false
            // the 2nd time due to `setPageIsLoading`. However because the value of
            // `pageIsLoading` is `false`  on the 2nd time, then it won't trigger another re-render
            setPageIsLoading(false);
        }
    }, [isLoadingOrgDefaultContent, homeDetailsAsyncState.loading, setPageIsLoading, defaultOrg]);

    const headerImageUrl = useMemo(() => {
        return organizationInfo?.fields.headerImage.fields.file.url || '';
    }, [organizationInfo]);

    // NOTE: Don't render anything until data comes back
    if (isLoading) {
        return (
            <div className="px-8 md:px-16 lg:px-32">
                <Skeleton className="h-56 w-full" />
            </div>
        );
    }

    const renderHeader = () => (
        <div className={styles.headerLogoContainer}>
            {!isLoadingOrgDefaultContent && organizationInfo && (
                <img className={styles.headerLogoImg} src={headerImageUrl} />
            )}
            {isLoadingOrgDefaultContent && (
                <div className="max-w-xs">
                    <Skeleton height="12rem" />
                </div>
            )}
        </div>
    );

    const renderCoordinatorInfo = () => (
        <>
            {!isLoadingCoordinator && coordinator && (
                <div className="mt-2">
                    <div className="text-xs text-grey-2">Your coordinator is</div>
                    <div className="text-sm text-brand-dark truncate">
                        {coordinator.firstName} {coordinator.lastName}
                    </div>
                    <div className="text-sm underline text-brand-dark truncate">
                        {coordinator.email}
                    </div>
                </div>
            )}
            {isLoadingCoordinator && (
                <div className="max-w-xs my-4">
                    <Skeleton height="1rem" />
                    <Skeleton />
                    <Skeleton />
                </div>
            )}
        </>
    );

    return (
        <div className="px-8 md:px-16 lg:px-32">
            <div className="md:flex gap-4 relative">
                {renderHeader()}
                <div className="w-full">
                    {!isLoading && (
                        <div className="text-xl text-brand font-bold py-2">{defaultOrg?.name}</div>
                    )}
                    {renderCoordinatorInfo()}
                </div>
            </div>
        </div>
    );
};
